import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { ja } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { AlertCircle, Send, Save, Clock, History, CalendarDays, Rows3, Trash2, Plus, ChevronDown, ChevronUp, Search, Phone } from "lucide-react";
import { deriveOrderUnit } from "@/lib/unit";
import { getSeasonalStatusOption, isHiddenFromStore } from "@/lib/seasonal";
import { sortCategories } from "@/lib/categoryOrder";
import { fuzzyMatchJa } from "@/lib/japaneseSearch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const parseDate = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const fmt = (d) => format(d, "yyyy-MM-dd");
const dowJp = (s) => format(parseDate(s), "EEE", { locale: ja });

// 変更申請でも通常発注と同じく「商品の登録単位」に固定する（顧客は単位を変更できない）
const productOrderUnit = (p) => {
  if (!p) return "g";
  const u =
    (p.unit && String(p.unit).trim()) ||
    (p.default_order_unit && String(p.default_order_unit).trim());
  return u || "g";
};

export default function OrderChangePage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [orders, setOrders] = useState([]);
  const [otherOrders, setOtherOrders] = useState([]);
  const [ooEdits, setOoEdits] = useState({}); // {oo_id: {quantity, unit, dirty}}
  const [ooDeletes, setOoDeletes] = useState({}); // {oo_id: true} 締切後の削除申請予定
  const [selectedDate, setSelectedDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [historyOrder, setHistoryOrder] = useState(null);
  const [changeRequests, setChangeRequests] = useState([]);
  const [viewMode, setViewMode] = useState("daily"); // 'daily' | 'weekly'
  const [selectedWeek, setSelectedWeek] = useState(0);
  // 商品追加パネル
  const [addPanelOpen, setAddPanelOpen] = useState(false);
  const [addCategoryFilter, setAddCategoryFilter] = useState("all");
  const [addSearchTerm, setAddSearchTerm] = useState("");
  const [addInputs, setAddInputs] = useState({}); // {product_id: {quantity, unit, staged?}}
  // 削除予定の order_id セット（保留中、申請送信時に確定）
  const [pendingDeletes, setPendingDeletes] = useState({}); // {order_id: true}
  // 変更申請依頼ダイアログ
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // 一括変更申請データ
  const [batchRequests, setBatchRequests] = useState([]);

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes, schedulesRes, ordersRes, crRes, batchRes, otherRes] = await Promise.all([
        axios.get(`${API}/products`, getAuthHeaders()),
        axios.get(`${API}/categories`, getAuthHeaders()),
        axios.get(`${API}/delivery-schedules`, getAuthHeaders()),
        axios.get(`${API}/orders`, getAuthHeaders()),
        axios.get(`${API}/orders/change-requests`, getAuthHeaders()),
        axios.get(`${API}/orders/change-requests/batch`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/other-orders`, getAuthHeaders()).catch(() => ({ data: [] })),
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setSchedules(schedulesRes.data);
      setOrders(ordersRes.data);
      setChangeRequests(crRes.data);
      setBatchRequests(Array.isArray(batchRes.data) ? batchRes.data : []);
      setOtherOrders(Array.isArray(otherRes.data) ? otherRes.data : []);

      if (!selectedDate && schedulesRes.data.length > 0) {
        const today = new Date().toISOString().split("T")[0];
        const sorted = [...schedulesRes.data].sort((a, b) =>
          a.delivery_date.localeCompare(b.delivery_date)
        );
        const upcoming = sorted.find((s) => s.delivery_date >= today);
        setSelectedDate(upcoming ? upcoming.delivery_date : sorted[0].delivery_date);
      }
    } catch (e) {
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const productsMap = useMemo(() => {
    const m = {};
    products.forEach((p) => (m[p.id] = p));
    return m;
  }, [products]);

  const sortedSchedules = useMemo(
    () => [...schedules].sort((a, b) => a.delivery_date.localeCompare(b.delivery_date)),
    [schedules]
  );

  const scheduleDateSet = useMemo(
    () => new Set(sortedSchedules.map((s) => s.delivery_date)),
    [sortedSchedules]
  );

  const scheduleByDate = useMemo(() => {
    const m = {};
    sortedSchedules.forEach((s) => {
      m[s.delivery_date] = s;
    });
    return m;
  }, [sortedSchedules]);

  // 週グループ化（月〜土）
  const weekGroups = useMemo(() => {
    const map = new Map();
    sortedSchedules.forEach((schedule) => {
      const d = parseDate(schedule.delivery_date);
      const dow = d.getDay();
      if (dow === 0) return;
      const days = dow === 0 ? 6 : dow - 1;
      const monday = new Date(d);
      monday.setDate(d.getDate() - days);
      const key = fmt(monday);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(schedule);
    });
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([, list]) => list);
  }, [sortedSchedules]);

  // 初期週: 選択日付に対応する週
  useEffect(() => {
    if (selectedDate && weekGroups.length > 0) {
      const idx = weekGroups.findIndex((w) =>
        w.some((s) => s.delivery_date === selectedDate)
      );
      if (idx >= 0) setSelectedWeek(idx);
    }
  }, [selectedDate, weekGroups]);

  const selectedSchedule = scheduleByDate[selectedDate];

  // 水曜は事務所休み: 翌日(木曜)納品分を開いたらポップアラート
  const [officeClosedPopup, setOfficeClosedPopup] = useState(false);
  useEffect(() => {
    const jst = new Date(Date.now() + 9 * 3600 * 1000);
    if (jst.getUTCDay() !== 3 || !selectedDate) return;
    const tomorrow = new Date(jst.getTime() + 24 * 3600 * 1000).toISOString().slice(0, 10);
    if (selectedDate === tomorrow) setOfficeClosedPopup(true);
  }, [selectedDate]);

  const isChangeLocked = (schedule) => {
    const dl = schedule?.change_deadline || schedule?.deadline;
    if (!dl) return false;
    return new Date() > new Date(dl.replace(" ", "T"));
  };

  const isOrderDeadlinePassed = (schedule) => {
    const dl = schedule?.deadline;
    if (!dl) return false;
    return new Date() > new Date(dl.replace(" ", "T"));
  };

  const locked = isChangeLocked(selectedSchedule);
  const orderPassed = isOrderDeadlinePassed(selectedSchedule);

  const dayOrders = orders.filter((o) => o.delivery_date === selectedDate);

  // 事前取込(魚など)でデスクへ送信済みの明細は顧客から変更・キャンセル不可（電話対応）
  const isEpLocked = (order) => !!order.confirmed && !order.cancelled && !!productsMap[order.product_id]?.early_pull;
  const isOoEpLocked = (oo) => {
    if (!oo.confirmed || oo.cancelled) return false;
    const lp = productsMap[oo.linked_product_id] || products.find((p) => oo.linked_product_code && p.code === oo.linked_product_code);
    return !!lp?.early_pull;
  };
  const sortedDayOrders = useMemo(
    () =>
      [...dayOrders].sort((a, b) => {
        const pa = productsMap[a.product_id];
        const pb = productsMap[b.product_id];
        return (pa?.code || "").localeCompare(pb?.code || "");
      }),
    [dayOrders, productsMap]
  );

  const pendingRequestIds = new Set(
    changeRequests.filter((r) => r.status === "pending").map((r) => r.order_id)
  );

  // この日付の連絡事項（通常発注のcommentsを集約・重複除去）
  const dayComments = useMemo(() => {
    const seen = [];
    dayOrders.forEach((o) => {
      const c = (o.comments || "").trim();
      if (c && !seen.includes(c)) seen.push(c);
    });
    return seen;
  }, [dayOrders]);

  // 一括変更申請: pending な対象 order_id とこの日に対する pending 状態
  const pendingBatchOrderIds = useMemo(() => {
    const set = new Set();
    batchRequests.forEach((b) => {
      if (b.status !== "pending") return;
      if (b.delivery_date !== selectedDate) return;
      (b.items || []).forEach((it) => {
        if (it.order_id) set.add(it.order_id);
        if (it.other_order_id) set.add(it.other_order_id);
      });
    });
    return set;
  }, [batchRequests, selectedDate]);

  // この日付のリストなし・季節発注
  const dayOtherOrders = useMemo(
    () =>
      otherOrders
        .filter((o) => o.delivery_date === selectedDate)
        .sort((a, b) => (a.product_name || "").localeCompare(b.product_name || "")),
    [otherOrders, selectedDate]
  );

  const dayHasPendingBatch = useMemo(() => {
    return batchRequests.some(
      (b) => b.status === "pending" && b.delivery_date === selectedDate
    );
  }, [batchRequests, selectedDate]);

  // 承認済の変更履歴を持つ order_id（過去30日以内）
  const recentlyApprovedOrderIds = useMemo(() => {
    const set = new Set();
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    orders.forEach((o) => {
      const hist = o.history || [];
      const lastApprove = [...hist]
        .reverse()
        .find((h) => h.action === "change_approved");
      if (lastApprove) {
        const t = new Date(lastApprove.timestamp).getTime();
        if (t >= cutoff) set.add(o.id);
      }
    });
    return set;
  }, [orders]);

  const setEdit = (orderId, field, value) => {
    setEdits((prev) => {
      const order = sortedDayOrders.find((o) => o.id === orderId);
      const product = order ? productsMap[order.product_id] : null;
      const baseQty = order ? order.quantity.toFixed(2) : "";
      const baseUnit = order ? order.unit || productOrderUnit(product) : "";
      const existing = prev[orderId];
      return {
        ...prev,
        [orderId]: {
          quantity: existing?.quantity ?? baseQty,
          unit: existing?.unit ?? baseUnit,
          ...(existing || {}),
          [field]: value,
          dirty: true,
        },
      };
    });
  };

  const getEdit = (order) => {
    const e = edits[order.id];
    if (e) return e;
    const product = productsMap[order.product_id];
    return {
      quantity: order.quantity.toFixed(2),
      unit: order.unit || productOrderUnit(product),
      dirty: false,
    };
  };

  // 発注締切前: 変更・追加・削除をその場で反映（承認不要）
  const handleSaveDirect = async () => {
    if (!batchItems.length) {
      toast.error("変更内容がありません");
      return;
    }
    setSubmitting(true);
    let failed = 0;
    const adds = [];
    const fail = (e) => {
      failed += 1;
      toast.error(e.response?.data?.detail || "保存に失敗しました");
    };
    for (const it of batchItems) {
      try {
        if (it.action === "delete" || (it.action === "modify" && !(it.new_quantity > 0))) {
          await axios.delete(`${API}/orders/${it.order_id}`, getAuthHeaders());
        } else if (it.action === "modify") {
          await axios.put(`${API}/orders/${it.order_id}`, { quantity: it.new_quantity, unit: it.new_unit }, getAuthHeaders());
        } else if (it.action === "add") {
          const ex = dayOrders.find((o) => o.product_id === it.product_id);
          if (ex) {
            await axios.put(`${API}/orders/${ex.id}`, { quantity: it.new_quantity, unit: it.new_unit }, getAuthHeaders());
          } else {
            adds.push({
              store_id: user.store_id,
              product_id: it.product_id,
              usage_date: selectedDate,
              delivery_date: selectedDate,
              quantity: it.new_quantity,
              unit: it.new_unit,
              line_no: 0,
            });
          }
        }
      } catch (e) {
        fail(e);
      }
    }
    if (adds.length) {
      try {
        await axios.post(`${API}/orders/bulk`, { orders: adds }, getAuthHeaders());
      } catch (e) {
        fail(e);
      }
    }
    if (!failed) toast.success("変更を保存しました");
    setEdits({});
    setPendingDeletes({});
    setAddInputs({});
    setSubmitting(false);
    fetchData();
  };

  // 削除予定トグル
  const [deleteTarget, setDeleteTarget] = useState(null);

  const togglePendingDelete = (orderId) => {
    setPendingDeletes((prev) => {
      const n = { ...prev };
      if (n[orderId]) delete n[orderId];
      else n[orderId] = true;
      return n;
    });
  };

  const confirmDelete = async () => {
    // 旧: 即時削除エンドポイント。新ワークフローでは pendingDeletes に追加するだけ
    if (!deleteTarget) return;
    togglePendingDelete(deleteTarget.id);
    setDeleteTarget(null);
  };

  // ── リストなし・季節発注(other_orders)の編集ハンドラ ──
  const getOoEdit = (oo) => {
    const e = ooEdits[oo.id];
    if (e) return e;
    return { quantity: Number(oo.quantity).toFixed(2), unit: oo.unit || "g", dirty: false };
  };
  const setOoEdit = (id, field, value) => {
    setOoEdits((prev) => {
      const oo = dayOtherOrders.find((o) => o.id === id);
      const base = {
        quantity: oo ? Number(oo.quantity).toFixed(2) : "",
        unit: oo ? oo.unit || "g" : "g",
      };
      const existing = prev[id];
      return { ...prev, [id]: { ...base, ...(existing || {}), [field]: value, dirty: true } };
    });
  };
  const toggleOoDelete = (id) => {
    setOoDeletes((prev) => {
      const n = { ...prev };
      if (n[id]) delete n[id];
      else n[id] = true;
      return n;
    });
  };
  // 締切前: 即時変更
  const handleOoSaveImmediate = async (oo) => {
    const e = ooEdits[oo.id];
    if (!e || !e.dirty) {
      toast.error("変更内容がありません");
      return;
    }
    const qty = parseFloat(e.quantity);
    if (isNaN(qty) || qty <= 0) {
      toast.error("数量を入力してください");
      return;
    }
    try {
      await axios.put(
        `${API}/other-orders/${oo.id}`,
        { quantity: qty, unit: e.unit },
        getAuthHeaders()
      );
      toast.success("変更しました");
      setOoEdits((prev) => {
        const n = { ...prev };
        delete n[oo.id];
        return n;
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "変更に失敗しました");
    }
  };
  // 締切前: 即時キャンセル（削除）
  const handleOoCancelImmediate = async (oo) => {
    if (!window.confirm(`「${oo.product_name}」の発注をキャンセルしますか？`)) return;
    try {
      await axios.delete(`${API}/other-orders/${oo.id}`, getAuthHeaders());
      toast.success("キャンセルしました");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "キャンセルに失敗しました");
    }
  };

  const [reqDialogOpen, setReqDialogOpen] = useState(false);
  const [reqTarget, setReqTarget] = useState(null);
  const [reqReason, setReqReason] = useState("");

  const openRequestDialog = (order) => {
    setReqTarget(order);
    setReqReason("");
    setReqDialogOpen(true);
  };

  const handleSubmitRequest = async () => {
    // 旧: 単一 change_request 送信。新ワークフローでは未使用。
    setReqDialogOpen(false);
  };

  // ── 商品追加パネル ──
  const orderedProductIds = useMemo(
    () => new Set(dayOrders.map((o) => o.product_id)),
    [dayOrders]
  );

  const categoriesMap = useMemo(() => {
    const m = {};
    categories.forEach((c) => (m[c.id] = c));
    return m;
  }, [categories]);

  const availableProducts = useMemo(() => {
    return products.filter((p) => !isHiddenFromStore(p));
  }, [products]);

  const filteredAddProducts = useMemo(() => {
    let list = availableProducts;
    if (addCategoryFilter !== "all") {
      list = list.filter((p) =>
        addCategoryFilter === "__seasonal__"
          ? p.is_seasonal
          : p.category_id === addCategoryFilter
      );
    }
    if (addSearchTerm.trim()) {
      list = list.filter((p) => fuzzyMatchJa(addSearchTerm, p.name, p.code, (p.search_keywords || []).join(" ")));
    }
    return [...list].sort((a, b) => (a.code || "").localeCompare(b.code || ""));
  }, [availableProducts, addCategoryFilter, addSearchTerm]);

  const groupedAddProducts = useMemo(() => {
    const groups = new Map();
    filteredAddProducts.forEach((p) => {
      const cname = categoriesMap[p.category_id]?.name || "その他";
      if (!groups.has(cname)) groups.set(cname, []);
      groups.get(cname).push(p);
    });
    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0], "ja"));
  }, [filteredAddProducts, categoriesMap]);

  const setAddInput = (pid, field, value) => {
    setAddInputs((prev) => ({
      ...prev,
      [pid]: {
        quantity: prev[pid]?.quantity ?? "",
        unit: prev[pid]?.unit ?? "",
        ...prev[pid],
        [field]: value,
      },
    }));
  };

  const handleAddProduct = (product) => {
    // 申請依頼バッチに含めるため、addInputs に必須項目を保存（送信時にまとめて反映）
    if (!user.store_id) {
      toast.error("店舗情報を取得できません");
      return;
    }
    const inp = addInputs[product.id] || {};
    const qty = parseFloat(inp.quantity);
    if (isNaN(qty) || qty <= 0) {
      toast.error("数量を入力してください");
      return;
    }
    const unit = productOrderUnit(product);
    setAddInputs((prev) => ({
      ...prev,
      [product.id]: { quantity: qty.toString(), unit, staged: true },
    }));
    toast.success(`${product.name} を申請に追加しました`);
  };

  const removeStagedAdd = (productId) => {
    setAddInputs((prev) => {
      const n = { ...prev };
      delete n[productId];
      return n;
    });
  };

  // ── 一括変更申請の集計 ──
  const buildBatchItems = () => {
    const items = [];
    // 数量変更（modify）
    sortedDayOrders.forEach((order) => {
      if (pendingDeletes[order.id] || isEpLocked(order)) return; // 削除予定・事前取込済みはmodifyに含めない
      const e = edits[order.id];
      if (!e || !e.dirty) return;
      const newQty = parseFloat(e.quantity);
      const product = productsMap[order.product_id];
      const oldUnit = order.unit || productOrderUnit(product);
      if (isNaN(newQty)) return;
      if (newQty === order.quantity && e.unit === oldUnit) return;
      items.push({
        action: "modify",
        order_id: order.id,
        product_id: order.product_id,
        old_quantity: order.quantity,
        new_quantity: newQty,
        old_unit: oldUnit,
        new_unit: e.unit,
      });
    });
    // 削除（delete）
    Object.keys(pendingDeletes).forEach((orderId) => {
      const order = sortedDayOrders.find((o) => o.id === orderId);
      if (!order || isEpLocked(order)) return;
      const product = productsMap[order.product_id];
      items.push({
        action: "delete",
        order_id: order.id,
        product_id: order.product_id,
        old_quantity: order.quantity,
        old_unit: order.unit || productOrderUnit(product),
      });
    });
    // 追加（add）
    Object.entries(addInputs).forEach(([pid, inp]) => {
      if (!inp || !inp.staged) return;
      const qty = parseFloat(inp.quantity);
      if (isNaN(qty) || qty <= 0) return;
      const product = productsMap[pid];
      items.push({
        action: "add",
        product_id: pid,
        new_quantity: qty,
        new_unit: productOrderUnit(product),
        is_new_line: !sortedDayOrders.some((o) => o.product_id === pid),
      });
    });
    // リストなし・季節発注（発注締切後のみ変更申請として計上）
    if (orderPassed) {
      dayOtherOrders.forEach((oo) => {
        if (isOoEpLocked(oo)) return;
        if (ooDeletes[oo.id]) {
          items.push({
            action: "delete",
            other_order_id: oo.id,
            product_name: oo.product_name,
            old_quantity: oo.quantity,
            old_unit: oo.unit,
          });
          return;
        }
        const e = ooEdits[oo.id];
        if (!e || !e.dirty) return;
        const newQty = parseFloat(e.quantity);
        if (isNaN(newQty)) return;
        if (newQty === oo.quantity) return;
        items.push({
          action: "modify",
          other_order_id: oo.id,
          product_name: oo.product_name,
          old_quantity: oo.quantity,
          new_quantity: newQty,
          old_unit: oo.unit,
          new_unit: oo.unit,
        });
      });
    }
    return items;
  };

  const batchItems = useMemo(buildBatchItems, [edits, pendingDeletes, addInputs, sortedDayOrders, productsMap, orderPassed, dayOtherOrders, ooEdits, ooDeletes]);

  const submitBatchRequest = async () => {
    if (!batchItems.length) {
      toast.error("申請する変更がありません");
      return;
    }
    if (!reqReason.trim()) {
      toast.error("変更理由を入力してください");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/orders/change-requests/batch`,
        {
          delivery_date: selectedDate,
          items: batchItems,
          reason: reqReason.trim(),
        },
        getAuthHeaders()
      );
      toast.success("変更依頼を送信しました");
      setSubmitDialogOpen(false);
      setReqReason("");
      setEdits({});
      setPendingDeletes({});
      setAddInputs({});
      setOoEdits({});
      setOoDeletes({});
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "送信に失敗しました");
    } finally {
      setSubmitting(false);
    }
  };

  // ── インラインカレンダー ──
  const calendarDates = useMemo(
    () => sortedSchedules.map((s) => parseDate(s.delivery_date)),
    [sortedSchedules]
  );

  const renderInlineCalendar = () => (
    <div className="inline-block bg-white border border-slate-200 rounded-sm shadow-sm">
      <Calendar
        mode="single"
        locale={ja}
        selected={selectedDate ? parseDate(selectedDate) : undefined}
        onSelect={(d) => {
          if (!d) return;
          setSelectedDate(fmt(d));
          setEdits({});
        }}
        modifiers={{ available: calendarDates }}
        modifiersClassNames={{
          available: "bg-emerald-50 text-emerald-700 font-semibold hover:bg-emerald-100",
        }}
        disabled={(date) => !scheduleDateSet.has(fmt(date))}
        initialFocus={false}
      />
      <div className="border-t border-slate-200 px-3 py-2 text-xs text-slate-600 bg-slate-50 flex items-center gap-2">
        <span className="inline-block w-3 h-3 rounded-sm bg-emerald-100 border border-emerald-300" />
        配送日あり
      </div>
    </div>
  );

  // ── 週間履歴ビュー ──
  const currentWeek = weekGroups[selectedWeek] || [];
  const weekOrdersByDate = useMemo(() => {
    const map = {};
    currentWeek.forEach((s) => {
      map[s.delivery_date] = orders
        .filter((o) => o.delivery_date === s.delivery_date)
        .sort((a, b) => {
          const pa = productsMap[a.product_id];
          const pb = productsMap[b.product_id];
          return (pa?.code || "").localeCompare(pb?.code || "");
        });
    });
    return map;
  }, [currentWeek, orders, productsMap]);

  const renderWeeklyHistory = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-sm text-slate-700">表示週:</Label>
        <select
          data-testid="change-week-select"
          value={selectedWeek}
          onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
          className="h-9 px-3 rounded-sm border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {weekGroups.map((ws, i) => (
            <option key={i} value={i}>
              第{i + 1}週 ({ws[0]?.delivery_date} 〜 {ws[ws.length - 1]?.delivery_date})
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {currentWeek.map((s) => {
          const ords = weekOrdersByDate[s.delivery_date] || [];
          const total = ords.reduce((sum, o) => sum + (o.quantity || 0), 0);
          const sLocked = isChangeLocked(s);
          return (
            <div
              key={s.id}
              className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden"
              data-testid={`week-card-${s.delivery_date}`}
            >
              <div
                className={`px-3 py-2 border-b flex items-center justify-between text-sm ${
                  sLocked
                    ? "bg-red-50 border-red-200 text-red-700"
                    : "bg-emerald-50 border-emerald-200 text-emerald-700"
                }`}
              >
                <div className="font-semibold">
                  {s.delivery_date} ({dowJp(s.delivery_date)})
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedDate(s.delivery_date);
                    setViewMode("daily");
                    setEdits({});
                  }}
                  data-testid={`open-day-${s.delivery_date}`}
                  className="text-xs px-2 py-0.5 rounded-sm bg-white border border-current hover:opacity-80"
                >
                  この日を編集
                </button>
              </div>
              <div className="p-3">
                {ords.length === 0 ? (
                  <div className="text-xs text-slate-400 py-2">発注なし</div>
                ) : (
                  <ul className="space-y-1.5 max-h-64 overflow-y-auto">
                    {ords.map((o) => {
                      const p = productsMap[o.product_id] || {};
                      const pending = pendingRequestIds.has(o.id);
                      return (
                        <li
                          key={o.id}
                          className="flex items-center justify-between text-xs border-b border-slate-100 pb-1"
                        >
                          <div className="min-w-0">
                            <div className="font-medium text-slate-700 truncate">
                              {p.name}
                            </div>
                            <div className="text-[10px] text-slate-400 font-mono">
                              {p.code}
                            </div>
                          </div>
                          <div className="text-right shrink-0 ml-2">
                            <span className="font-mono font-semibold text-slate-800">
                              {o.quantity.toFixed(2)} {o.unit || deriveOrderUnit(p)}
                            </span>
                            {pending && (
                              <span className="ml-1 text-amber-600 text-[10px]">
                                申請中
                              </span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
                  <span>{ords.length} 件</span>
                  <span className="font-mono font-semibold text-slate-700">
                    合計: {total.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h2 className="text-xl font-semibold font-chivo tracking-tight text-slate-800">
            発注確認＆変更
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            日付を選んで既存の発注を変更できます。※変更締切は納品前日の12:00まで（水曜は事務所休みのため、木曜納品分は火曜12:00まで）です。
          </p>
          <p className="text-xs text-amber-700 mt-1">
            ※追加や変更に関しては承れない場合がございます。その場合はご了承ください。
          </p>
        </div>
        <div className="inline-flex rounded-sm border border-slate-300 overflow-hidden">
          <button
            data-testid="change-mode-daily"
            onClick={() => setViewMode("daily")}
            className={`h-9 px-3 text-sm flex items-center gap-1.5 ${
              viewMode === "daily" ? "bg-slate-900 text-white" : "bg-white text-slate-700"
            }`}
          >
            <CalendarDays className="h-4 w-4" />
            日ごと
          </button>
          <button
            data-testid="change-mode-weekly"
            onClick={() => setViewMode("weekly")}
            className={`h-9 px-3 text-sm flex items-center gap-1.5 ${
              viewMode === "weekly" ? "bg-slate-900 text-white" : "bg-white text-slate-700"
            }`}
          >
            <Rows3 className="h-4 w-4" />
            週間履歴
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">読み込み中...</div>
      ) : viewMode === "weekly" ? (
        renderWeeklyHistory()
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[auto,1fr] gap-4 items-start">
          {/* インラインカレンダー */}
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1.5 block">
              配送日を選択
            </Label>
            {renderInlineCalendar()}
          </div>

          {/* 編集テーブル */}
          <div className="space-y-3">
            {selectedSchedule && (
              <div
                className={`rounded-sm border p-3 text-sm flex items-center gap-2 ${
                  locked
                    ? "border-red-200 bg-red-50 text-red-700"
                    : orderPassed
                    ? "border-amber-200 bg-amber-50 text-amber-700"
                    : "border-emerald-200 bg-emerald-50 text-emerald-700"
                }`}
                data-testid="change-deadline-banner"
              >
                {locked || orderPassed ? (
                  <AlertCircle className="h-4 w-4 shrink-0" />
                ) : (
                  <Clock className="h-4 w-4 shrink-0" />
                )}
                <span>
                  <span className="font-semibold mr-2">
                    {selectedDate} ({dowJp(selectedDate)})
                  </span>
                  {locked
                    ? `変更締切 ${selectedSchedule.change_deadline || "-"} を過ぎました（操作不可）`
                    : orderPassed
                    ? `変更締切 ${selectedSchedule.change_deadline || "-"} まで（発注締切後のため変更・キャンセルは変更申請で受付）`
                    : `変更締切 ${selectedSchedule.change_deadline || "-"} まで（発注締切前は即時変更・追加・削除可能）`}
                </span>
              </div>
            )}

            {selectedSchedule && dayComments.length > 0 && (
              <div
                data-testid="day-comments"
                className="bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 text-sm"
              >
                <span className="font-semibold text-amber-800">💬 連絡事項:</span>{" "}
                <span className="text-amber-900 whitespace-pre-wrap">{dayComments.join(" / ")}</span>
              </div>
            )}

            {selectedSchedule && !locked && (sortedDayOrders.some(isEpLocked) || dayOtherOrders.some(isOoEpLocked)) && (
              <div data-testid="ep-locked-notice" className="px-4 py-3 text-sm text-sky-800 bg-sky-50 border border-sky-200 rounded-sm flex items-center gap-2">
                <Phone className="h-4 w-4 shrink-0" />
                「事前取込済」の商品（魚など）はすでに手配済みのため、変更・キャンセルはお電話でお問い合わせください。
              </div>
            )}

            {sortedDayOrders.length === 0 ? (
              <div className="text-center py-12 text-slate-500 border border-slate-200 rounded-sm bg-white">
                この日付の発注はありません
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                    <tr>
                      <th className="h-10 px-4 text-left font-medium text-slate-500 text-xs uppercase">
                        商品
                      </th>
                      <th className="h-10 px-4 text-right font-medium text-slate-500 text-xs uppercase w-32">
                        現数量
                      </th>
                      <th className="h-10 px-4 text-center font-medium text-slate-500 text-xs uppercase w-32">
                        新単位
                      </th>
                      <th className="h-10 px-4 text-right font-medium text-slate-500 text-xs uppercase w-32">
                        新数量
                      </th>
                      <th className="h-10 px-4 text-center font-medium text-slate-500 text-xs uppercase w-44">
                        操作
                      </th>
                      <th className="h-10 px-4 text-center font-medium text-slate-500 text-xs uppercase w-16">
                        履歴
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDayOrders.map((order) => {
                      const product = productsMap[order.product_id] || {};
                      const edit = getEdit(order);
                      const pending = pendingRequestIds.has(order.id);
                      const isPendingBatch = pendingBatchOrderIds.has(order.id);
                      const isApproved = recentlyApprovedOrderIds.has(order.id);
                      const isMarkedDelete = !!pendingDeletes[order.id];
                      const epLocked = isEpLocked(order);
                      const changed =
                        edit.dirty &&
                        (parseFloat(edit.quantity) !== order.quantity ||
                          edit.unit !== (order.unit || productOrderUnit(product)));
                      return (
                        <tr
                          key={order.id}
                          className={`border-b border-slate-100 hover:bg-slate-50/50 ${
                            isMarkedDelete ? "bg-red-50 opacity-70" : ""
                          }`}
                        >
                          <td className="p-2 px-4 align-middle text-sm">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className={`font-medium ${isMarkedDelete ? "line-through text-red-600" : ""}`}>{product.name || "-"}</span>
                              {isPendingBatch && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 border border-amber-200">
                                  変更申請中
                                </span>
                              )}                              {!isPendingBatch && isApproved && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700 border border-emerald-200">
                                  変更済
                                </span>
                              )}
                              {isMarkedDelete && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700 border border-red-200">
                                  削除予定
                                </span>
                              )}
                              {epLocked && (
                                <span data-testid={`ep-locked-badge-${order.id}`} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-sky-100 text-sky-700 border border-sky-200">
                                  事前取込済
                                </span>
                              )}
                              {changed && !isMarkedDelete && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 border border-amber-200">
                                  変更あり
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500">{product.code}</div>
                          </td>
                          <td className="p-2 px-4 align-middle text-sm font-mono text-right">
                            {order.quantity.toFixed(2)} {order.unit || productOrderUnit(product)}
                          </td>
                          <td className="p-1 px-2 align-middle">
                            <span
                              data-testid={`change-unit-${order.id}`}
                              className="h-9 w-full inline-flex items-center justify-center border border-slate-200 bg-slate-50 rounded-sm text-sm text-slate-600 font-mono"
                              title="単位は商品の登録単位に固定されています"
                            >
                              {order.unit || productOrderUnit(product)}
                            </span>
                          </td>
                          <td className="p-1 px-2 align-middle">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={edit.quantity}
                              disabled={locked || isMarkedDelete || epLocked}
                              onChange={(e) => setEdit(order.id, "quantity", e.target.value)}
                              data-testid={`change-qty-${order.id}`}
                              className={`h-9 w-full text-right font-mono rounded-sm ${
                                changed ? "border-amber-400" : "border-slate-300"
                              }`}
                            />
                          </td>
                          <td className="p-2 px-4 align-middle text-center">
                            {locked ? (
                              <span className="text-xs text-slate-400">締切済</span>
                            ) : epLocked ? (
                              <span data-testid={`ep-locked-phone-${order.id}`} className="inline-flex items-center gap-1 text-xs text-sky-700">
                                <Phone className="h-3.5 w-3.5" />お電話で
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => togglePendingDelete(order.id)}
                                data-testid={`delete-order-${order.id}`}
                                title={isMarkedDelete ? "削除予定を取消" : "削除予定にする"}
                                className={`inline-flex items-center justify-center h-8 px-2 rounded-sm text-xs ${
                                  isMarkedDelete
                                    ? "bg-red-100 text-red-700 border border-red-300 hover:bg-red-200"
                                    : "text-red-600 hover:bg-red-50 border border-transparent"
                                }`}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                {isMarkedDelete ? "取消" : "削除"}
                              </button>
                            )}
                          </td>
                          <td className="p-2 px-4 align-middle text-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setHistoryOrder(order)}
                              data-testid={`history-${order.id}`}
                              className="h-8"
                            >
                              <History className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* その他の発注（リストなし・季節のおすすめ） */}
            {selectedSchedule && dayOtherOrders.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="other-orders-section">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-800">
                    その他の発注（リストなし・季節のおすすめ）
                  </h3>
                  <span className="text-xs text-slate-500">{dayOtherOrders.length} 件</span>
                </div>
                <div className="overflow-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="h-10 px-4 text-left font-medium text-slate-500 text-xs uppercase">品名</th>
                        <th className="h-10 px-4 text-right font-medium text-slate-500 text-xs uppercase w-28">現数量</th>
                        <th className="h-10 px-4 text-center font-medium text-slate-500 text-xs uppercase w-28">単位</th>
                        <th className="h-10 px-4 text-right font-medium text-slate-500 text-xs uppercase w-28">新数量</th>
                        <th className="h-10 px-4 text-center font-medium text-slate-500 text-xs uppercase w-56">操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dayOtherOrders.map((oo) => {
                        const oe = getOoEdit(oo);
                        const isPendingBatch = pendingBatchOrderIds.has(oo.id);
                        const isMarkedDelete = !!ooDeletes[oo.id];
                        const ooEp = isOoEpLocked(oo);
                        const changed =
                          oe.dirty && parseFloat(oe.quantity) !== oo.quantity;
                        return (
                          <tr
                            key={oo.id}
                            data-testid={`other-order-row-${oo.id}`}
                            className={`border-b border-slate-100 hover:bg-slate-50/50 ${isMarkedDelete ? "bg-red-50 opacity-70" : ""}`}
                          >
                            <td className="p-2 px-4 align-middle text-sm">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className={`font-medium ${isMarkedDelete ? "line-through text-red-600" : ""}`}>{oo.product_name}</span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-purple-100 text-purple-700 border border-purple-200">その他</span>
                                {isPendingBatch && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 border border-amber-200">変更申請中</span>
                                )}
                                {isMarkedDelete && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700 border border-red-200">キャンセル予定</span>
                                )}
                                {changed && !isMarkedDelete && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 border border-amber-200">変更あり</span>
                                )}
                              </div>
                              {oo.linked_product_name && (
                                <div className="text-xs text-emerald-700 mt-0.5 flex items-center gap-1" data-testid={`oo-linked-${oo.id}`}>
                                  <span className="text-slate-400">→</span>
                                  <span className="font-medium">{oo.linked_product_name}</span>
                                  <span className="text-slate-400">として登録</span>
                                </div>
                              )}
                              {oo.notes && <div className="text-xs text-slate-400 mt-0.5">{oo.notes}</div>}
                            </td>
                            <td className="p-2 px-4 align-middle text-sm font-mono text-right">
                              {Number(oo.quantity).toFixed(2)} {oo.unit || "g"}
                            </td>
                            <td className="p-1 px-2 align-middle text-center">
                              <span
                                data-testid={`oo-unit-${oo.id}`}
                                className="text-sm text-slate-600 font-mono"
                              >
                                {oo.unit || "g"}
                              </span>
                            </td>
                            <td className="p-1 px-2 align-middle">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={oe.quantity}
                                disabled={locked || isMarkedDelete || ooEp}
                                onChange={(e) => setOoEdit(oo.id, "quantity", e.target.value)}
                                data-testid={`oo-qty-${oo.id}`}
                                className={`h-9 w-full text-right font-mono rounded-sm ${changed ? "border-amber-400" : "border-slate-300"}`}
                              />
                            </td>
                            <td className="p-2 px-4 align-middle text-center">
                              {locked ? (
                                <span className="text-xs text-slate-400">締切済</span>
                              ) : ooEp ? (
                                <span data-testid={`oo-ep-locked-${oo.id}`} className="inline-flex items-center gap-1 text-xs text-sky-700">
                                  <Phone className="h-3.5 w-3.5" />事前取込済・お電話で
                                </span>
                              ) : !orderPassed ? (
                                <div className="inline-flex gap-1">
                                  <Button
                                    size="sm"
                                    onClick={() => handleOoSaveImmediate(oo)}
                                    disabled={!changed}
                                    data-testid={`oo-save-${oo.id}`}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-8 text-xs px-2"
                                  >
                                    <Save className="h-3.5 w-3.5 mr-1" />保存
                                  </Button>
                                  <button
                                    type="button"
                                    onClick={() => handleOoCancelImmediate(oo)}
                                    data-testid={`oo-cancel-${oo.id}`}
                                    className="inline-flex items-center justify-center h-8 px-2 rounded-sm text-xs text-red-600 hover:bg-red-50 border border-transparent"
                                  >
                                    <Trash2 className="h-4 w-4 mr-1" />キャンセル
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => toggleOoDelete(oo.id)}
                                  data-testid={`oo-cancel-${oo.id}`}
                                  title={isMarkedDelete ? "キャンセル予定を取消" : "キャンセル（削除申請）"}
                                  className={`inline-flex items-center justify-center h-8 px-2 rounded-sm text-xs ${isMarkedDelete ? "bg-red-100 text-red-700 border border-red-300 hover:bg-red-200" : "text-red-600 hover:bg-red-50 border border-transparent"}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  {isMarkedDelete ? "取消" : "キャンセル"}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {!locked && orderPassed && (
                  <div className="px-4 py-2 text-xs text-amber-700 bg-amber-50 border-t border-amber-200">
                    発注締切後のため、変更・キャンセルは下の「変更申請依頼を送信」で申請してください。
                  </div>
                )}
                {!locked && !orderPassed && (
                  <div className="px-4 py-2 text-xs text-emerald-700 bg-emerald-50 border-t border-emerald-100">
                    発注締切前のため、変更・キャンセルは下の「変更を保存」ですぐに反映されます。
                  </div>
                )}

              </div>
            )}

            {/* 商品追加パネル（折り畳み式） */}
            {selectedSchedule && !locked && (
              <div className="bg-white border border-emerald-200 rounded-sm shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => setAddPanelOpen((v) => !v)}
                  data-testid="toggle-add-panel"
                  className="w-full flex items-center justify-between px-4 py-3 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
                    <Plus className="h-4 w-4" />
                    商品を追加（リストから選択）
                  </span>
                  {addPanelOpen ? (
                    <ChevronUp className="h-4 w-4 text-emerald-700" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-emerald-700" />
                  )}
                </button>
                {addPanelOpen && (
                  <div className="p-4 space-y-3 border-t border-emerald-100">
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
                      ※追加や変更に関しては承れない場合がございます。その場合はご了承ください。
                    </div>

                    {/* カテゴリフィルタ + 検索 */}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setAddCategoryFilter("all")}
                        data-testid="add-cat-all"
                        className={`h-8 px-3 text-xs rounded-sm border transition-colors ${
                          addCategoryFilter === "all"
                            ? "bg-emerald-600 text-white border-emerald-600"
                            : "bg-white text-slate-700 border-slate-300 hover:border-emerald-400"
                        }`}
                      >
                        すべて
                      </button>
                      <button
                        type="button"
                        onClick={() => setAddCategoryFilter("__seasonal__")}
                        data-testid="add-cat-seasonal"
                        className={`h-8 px-3 text-xs rounded-sm border transition-colors ${
                          addCategoryFilter === "__seasonal__"
                            ? "bg-emerald-600 text-white border-emerald-600"
                            : "bg-white text-slate-700 border-slate-300 hover:border-emerald-400"
                        }`}
                      >
                        季節
                      </button>
                      {sortCategories(categories).map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setAddCategoryFilter(c.id)}
                          data-testid={`add-cat-${c.id}`}
                          className={`h-8 px-3 text-xs rounded-sm border transition-colors ${
                            addCategoryFilter === c.id
                              ? "bg-emerald-600 text-white border-emerald-600"
                              : "bg-white text-slate-700 border-slate-300 hover:border-emerald-400"
                          }`}
                        >
                          {c.name}
                        </button>
                      ))}
                      <div className="relative ml-auto">
                        <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                          value={addSearchTerm}
                          onChange={(e) => setAddSearchTerm(e.target.value)}
                          placeholder="商品名・コードで検索（ローマ字でもOK）"
                          data-testid="add-search-input"
                          lang="ja"
                          inputMode="text"
                          autoCapitalize="off"
                          style={{ imeMode: "active" }}
                          className="h-8 pl-8 w-56 text-sm rounded-sm"
                        />
                      </div>
                    </div>

                    {/* 商品リスト（カテゴリ別グループ） */}
                    <div className="max-h-[480px] overflow-y-auto border border-slate-200 rounded-sm">
                      {groupedAddProducts.length === 0 ? (
                        <div className="text-center py-8 px-4 text-sm text-slate-500" data-testid="add-no-products-hint">
                          {addSearchTerm.trim() ? (
                            <div className="space-y-2">
                              <p className="font-semibold text-slate-700">「{addSearchTerm.trim()}」が見つかりませんでした</p>
                              <ul className="text-xs text-slate-500 leading-relaxed inline-block text-left">
                                <li>・別の呼び方（ひらがな／カタカナ、略称）でもお試しください</li>
                                <li>・一部の漢字名は読みで検索できない場合があります</li>
                                <li>・見つからない商品は下の<span className="font-semibold text-amber-700">「その他の発注（リストなし発注）」</span>からご依頼ください</li>
                              </ul>
                            </div>
                          ) : (
                            "該当する商品がありません"
                          )}
                        </div>
                      ) : (
                        groupedAddProducts.map(([cname, plist]) => (
                          <div key={cname}>
                            <div className="bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600 sticky top-0">
                              {cname}（{plist.length}）
                            </div>
                            <ul>
                              {plist.map((p) => {
                                const inp = addInputs[p.id] || {};
                                const defaultUnit = productOrderUnit(p);
                                const isOrdered = orderedProductIds.has(p.id);
                                return (
                                  <li
                                    key={p.id}
                                    data-testid={`add-row-${p.id}`}
                                    className={`flex items-center gap-2 px-3 py-2 border-b border-slate-100 text-sm ${
                                      isOrdered ? "bg-slate-50" : "bg-white"
                                    }`}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-slate-700 truncate flex items-center gap-1.5">
                                        {p.name}
                                        {(() => {
                                          const opt = getSeasonalStatusOption(p.seasonal_status);
                                          if (opt) {
                                            return (
                                              <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${opt.badgeClass}`}>
                                                {opt.label}
                                              </span>
                                            );
                                          }
                                          if (p.is_seasonal) {
                                            return (
                                              <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 border border-amber-200">
                                                季節
                                              </span>
                                            );
                                          }
                                          return null;
                                        })()}
                                        {isOrdered && (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-700 border border-blue-200">
                                            発注済
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-[10px] text-slate-400 font-mono">
                                        {p.code}
                                      </div>
                                    </div>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={inp.quantity || ""}
                                      onChange={(e) =>
                                        setAddInput(p.id, "quantity", e.target.value)
                                      }
                                      placeholder="—"
                                      data-testid={`add-qty-${p.id}`}
                                      className="h-8 w-24 text-right font-mono rounded-sm border-slate-300"
                                    />
                                    <span
                                      data-testid={`add-unit-${p.id}`}
                                      className="h-8 w-20 shrink-0 inline-flex items-center justify-center border border-slate-200 bg-slate-50 rounded-sm text-sm text-slate-600 font-mono"
                                      title="単位は商品の登録単位に固定されています"
                                    >
                                      {defaultUnit}
                                    </span>
                                    <Button
                                      size="sm"
                                      onClick={() => handleAddProduct(p)}
                                      disabled={
                                        !inp.quantity ||
                                        parseFloat(inp.quantity) <= 0 ||
                                        inp.staged
                                      }
                                      data-testid={`add-submit-${p.id}`}
                                      className={`rounded-sm h-8 text-white ${
                                        inp.staged
                                          ? "bg-amber-500 hover:bg-amber-500"
                                          : "bg-emerald-600 hover:bg-emerald-700"
                                      }`}
                                    >
                                      {inp.staged ? (
                                        <>申請対象</>
                                      ) : (
                                        <>
                                          <Plus className="h-3.5 w-3.5 mr-1" />
                                          {isOrdered ? "上書き" : "追加"}
                                        </>
                                      )}
                                    </Button>
                                    {inp.staged && (
                                      <button
                                        type="button"
                                        onClick={() => removeStagedAdd(p.id)}
                                        data-testid={`add-cancel-${p.id}`}
                                        className="text-xs text-slate-500 hover:text-red-600 px-1"
                                        title="申請対象から外す"
                                      >
                                        ✕
                                      </button>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 変更申請依頼: 全ての変更をまとめて送信 */}
            {selectedSchedule && !locked && (
              <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4 sticky bottom-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-sm text-slate-600">
                    {batchItems.length === 0 ? (
                      <span>{orderPassed ? "変更内容を入力後、ボタンを押して変更依頼を送信してください。" : "変更内容を入力後、「変更を保存」を押すとすぐに反映されます。"}</span>
                    ) : (
                      <span className="text-slate-700">
                        {orderPassed ? "申請対象" : "保存対象"}: <span className="font-semibold text-amber-700">{batchItems.length}件</span>
                        <span className="ml-3 text-xs text-slate-500">
                          （変更{batchItems.filter((i) => i.action === "modify").length} / 追加{batchItems.filter((i) => i.action === "add").length} / 削除{batchItems.filter((i) => i.action === "delete").length}）
                        </span>
                      </span>
                    )}
                  </div>
                  {orderPassed ? (
                    <Button
                      size="lg"
                      onClick={() => setSubmitDialogOpen(true)}
                      disabled={batchItems.length === 0}
                      data-testid="open-submit-dialog"
                      className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      変更申請依頼を送信
                    </Button>
                  ) : (
                    <Button
                      size="lg"
                      onClick={handleSaveDirect}
                      disabled={batchItems.length === 0 || submitting}
                      data-testid="save-direct-btn"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {submitting ? "保存中..." : "変更を保存"}
                    </Button>
                  )}
                </div>
                {orderPassed && dayHasPendingBatch && (
                  <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
                    この日付は既に変更申請が処理待ちです。新しい申請を送信すると既存の申請に上書きされます。
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 変更申請依頼の確認ダイアログ */}
      <Dialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="submit-batch-dialog">
          <DialogHeader>
            <DialogTitle>変更依頼を送信</DialogTitle>
            <DialogDescription className="pt-2 text-slate-600">
              変更依頼を送信します。センターにて確認後、受付処理をいたします。場合によっては受付できない場合がございますのでご注意ください。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 max-h-72 overflow-y-auto text-sm">
            <div className="text-xs text-slate-500">
              配送日: <span className="font-mono text-slate-700">{selectedDate}</span> /
              申請件数: <span className="font-semibold text-amber-700">{batchItems.length}件</span>
            </div>
            <ul className="space-y-1">
              {batchItems.map((it, i) => {
                const p = productsMap[it.product_id];
                const label = it.action === "modify" ? "変更" : it.action === "add" ? (it.is_new_line ? "締切後発注" : "追加") : "削除";
                const color =
                  it.action === "modify"
                    ? "bg-amber-100 text-amber-700 border-amber-200"
                    : it.action === "add"
                    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                    : "bg-red-100 text-red-700 border-red-200";
                return (
                  <li
                    key={i}
                    className="flex items-center justify-between text-xs border-b border-slate-100 py-1"
                    data-testid={`batch-item-${i}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${color}`}>
                        {label}
                      </span>
                      <span className="font-medium truncate">{p?.name || "-"}</span>
                    </div>
                    <span className="font-mono text-slate-600 shrink-0 ml-2">
                      {it.action === "modify"
                        ? `${(it.old_quantity || 0).toFixed(2)} ${it.old_unit || ""} → ${(it.new_quantity || 0).toFixed(2)} ${it.new_unit || ""}`
                        : it.action === "add"
                        ? `+ ${(it.new_quantity || 0).toFixed(2)} ${it.new_unit || ""}`
                        : `- ${(it.old_quantity || 0).toFixed(2)} ${it.old_unit || ""}`}
                    </span>
                  </li>
                );
              })}
            </ul>
            <div>
              <Label htmlFor="batch-reason" className="text-xs">
                変更理由 <span className="text-red-600">必須</span>
              </Label>
              <Input
                id="batch-reason"
                value={reqReason}
                onChange={(e) => setReqReason(e.target.value)}
                placeholder="例: 仕入れ変更、お客様要望"
                data-testid="batch-reason-input"
                required
                className={`mt-1 h-9 rounded-sm ${!reqReason.trim() ? "border-red-300" : ""}`}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitDialogOpen(false)} className="rounded-sm">
              キャンセル
            </Button>
            <Button
              onClick={submitBatchRequest}
              disabled={submitting || batchItems.length === 0 || !reqReason.trim()}
              data-testid="confirm-submit-batch"
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
            >
              <Send className="h-4 w-4 mr-2" />
              {submitting ? "送信中..." : "依頼を送信"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 変更申請ダイアログ */}
      <Dialog open={reqDialogOpen} onOpenChange={setReqDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>変更申請</DialogTitle>
          </DialogHeader>
          {reqTarget && (
            <div className="space-y-3 text-sm">
              <div>
                <span className="text-slate-500">商品: </span>
                <span className="font-medium">
                  {productsMap[reqTarget.product_id]?.name}
                </span>
              </div>
              <div>
                <span className="text-slate-500">配送日: </span>
                <span>{reqTarget.delivery_date}</span>
              </div>
              <div>
                <span className="text-slate-500">変更: </span>
                <span className="font-mono">
                  {reqTarget.quantity.toFixed(2)} {reqTarget.unit} → {getEdit(reqTarget).quantity}{" "}
                  {getEdit(reqTarget).unit}
                </span>
              </div>
              <div>
                <Label htmlFor="reason">変更理由</Label>
                <Input
                  id="reason"
                  data-testid="change-reason-input"
                  value={reqReason}
                  onChange={(e) => setReqReason(e.target.value)}
                  placeholder="例: 仕入れ変更、お客様要望"
                  className="mt-1 h-9 rounded-sm"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReqDialogOpen(false)}
              className="rounded-sm"
            >
              キャンセル
            </Button>
            <Button
              onClick={handleSubmitRequest}
              data-testid="submit-change-request"
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
            >
              申請を送信
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 履歴ダイアログ */}
      <Dialog open={!!historyOrder} onOpenChange={(o) => !o && setHistoryOrder(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>変更履歴</DialogTitle>
          </DialogHeader>
          {historyOrder && (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {(historyOrder.history || []).length === 0 ? (
                <div className="text-sm text-slate-500">履歴なし</div>
              ) : (
                historyOrder.history.map((h, i) => (
                  <div key={i} className="text-xs border-l-2 border-slate-200 pl-3 py-1">
                    <div className="font-medium text-slate-700">
                      {new Date(h.timestamp).toLocaleString("ja-JP")} / {h.username} / {h.action}
                    </div>
                    <div className="text-slate-600">
                      {h.old_quantity != null
                        ? `${h.old_quantity} ${h.old_unit || ""}`
                        : "-"}{" "}
                      →{" "}
                      {h.new_quantity != null
                        ? `${h.new_quantity} ${h.new_unit || ""}`
                        : "-"}
                    </div>
                    {h.note && <div className="text-slate-500">備考: {h.note}</div>}
                  </div>
                ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-order-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">発注を削除しますか？</DialogTitle>
            <DialogDescription className="pt-2 text-slate-600">
              この操作は取り消せません。削除してもよろしいですか？
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm space-y-1">
              <div><span className="text-slate-500">商品:</span> <span className="font-medium">{productsMap[deleteTarget.product_id]?.name || "-"}</span></div>
              <div><span className="text-slate-500">納品日:</span> <span className="font-mono">{deleteTarget.delivery_date}</span></div>
              <div><span className="text-slate-500">数量:</span> <span className="font-mono">{deleteTarget.quantity?.toFixed(2)} {deleteTarget.unit || deriveOrderUnit(productsMap[deleteTarget.product_id])}</span></div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="delete-order-cancel">
              キャンセル
            </Button>
            <Button onClick={confirmDelete} data-testid="delete-order-confirm" className="bg-red-600 text-white hover:bg-red-700">
              <Trash2 className="h-4 w-4 mr-2" />
              削除する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={officeClosedPopup} onOpenChange={setOfficeClosedPopup}>
        <DialogContent data-testid="office-closed-popup" className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5 text-sky-700" />
              お電話でのご依頼をお願いします
            </DialogTitle>
            <DialogDescription className="text-slate-700 text-sm leading-relaxed pt-2">
              本日事務所がお休みのため翌日分（木曜日納品分）の変更がポータル上にて行えません、お手数ですがお電話にて変更のご依頼をお願い致します。
            </DialogDescription>
          </DialogHeader>
          <a href="tel:0338551610" data-testid="office-closed-phone" className="flex items-center justify-center gap-2 rounded-sm border border-sky-300 bg-sky-50 py-3 text-lg font-bold text-sky-800 hover:bg-sky-100">
            <Phone className="h-5 w-5" />03-3855-1610
          </a>
          <DialogFooter>
            <Button onClick={() => setOfficeClosedPopup(false)} data-testid="office-closed-popup-close" className="rounded-sm">
              閉じる
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
